import React, { useState } from 'react';
import {
  View,
  ScrollView,
  TouchableOpacity,
  TextInput,
  StyleSheet,
  useWindowDimensions,
} from 'react-native';
import Text from '../../components/text';

import { Ionicons } from '@expo/vector-icons';
import MediaNewsHub from './mediaNewsHub';
import MediaBlogHub from './mediaBlogHub';
import MediaVideoHub from './mediaVideoHub';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import { COLORS, SHADOWS } from '../../constants/theme';

const MediaHub = (props) => {
  const [currentTabIndex, setCurrentTabIndex] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  return (
    <View style={styles.root}>
      {/* Breadcrumb Bar */}
      <View style={[styles.breadcrumbBar, isMobile && styles.breadcrumbBarMobile]}>
        <Text style={styles.breadItem}>Back / Media Hub</Text>
        <Text style={styles.breadSep}>/</Text>
        <Text style={[styles.breadItem, styles.breadActive]}>
          {currentTabIndex === 1 ? 'Landlord Hub' : currentTabIndex === 2 ? 'News' : 'Videos'}
        </Text>
      </View>

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={[
          styles.scrollContent,
          isMobile && styles.scrollContentMobile,
        ]}
        showsVerticalScrollIndicator={true}
      >
        {/* Header Hero Section */}
        <View style={[styles.headerSection, isMobile && styles.headerSectionMobile]}>
          <View style={styles.headerTextWrap}>
            <Text style={styles.pageTitle}>Media Hub</Text>
            <Text style={styles.pageSubtitle}>
              Explore expert landlord guides, property market updates, and news.
            </Text>
          </View>

          {/* Search Box */}
          <View style={[styles.searchBox, isMobile && styles.searchBoxMobile]}>
            <Ionicons name="search-outline" size={16} color="#9CA3AF" style={{ marginRight: 8 }} />
            <TextInput
              placeholder="Search articles..."
              placeholderTextColor="#9CA3AF"
              value={searchQuery}
              onChangeText={setSearchQuery}
              style={styles.searchInput}
            />
            {searchQuery ? (
              <TouchableOpacity onPress={() => setSearchQuery('')} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                <Ionicons name="close-circle" size={16} color="#9CA3AF" />
              </TouchableOpacity>
            ) : null}
          </View>
        </View>

        {/* Tab Switcher Segmented Control */}
        <View style={[styles.tabBar, isMobile && styles.tabBarMobile]}>
          <TouchableOpacity
            onPress={() => setCurrentTabIndex(1)}
            style={[styles.tabBtn, isMobile && styles.tabBtnMobile, currentTabIndex === 1 && styles.tabBtnActive]}
            activeOpacity={0.8}
          >
            <Ionicons
              name="book-outline"
              size={15}
              color={currentTabIndex === 1 ? '#6C2BD9' : '#6B7280'}
              style={{ marginRight: 6 }}
            />
            <Text style={[styles.tabText, currentTabIndex === 1 && styles.tabTextActive]}>
              Landlord Hub
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => setCurrentTabIndex(2)}
            style={[styles.tabBtn, isMobile && styles.tabBtnMobile, currentTabIndex === 2 && styles.tabBtnActive]}
            activeOpacity={0.8}
          >
            <Ionicons
              name="newspaper-outline"
              size={15}
              color={currentTabIndex === 2 ? '#6C2BD9' : '#6B7280'}
              style={{ marginRight: 6 }}
            />
            <Text style={[styles.tabText, currentTabIndex === 2 && styles.tabTextActive]}>
              News
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => setCurrentTabIndex(3)}
            style={[styles.tabBtn, isMobile && styles.tabBtnMobile, currentTabIndex === 3 && styles.tabBtnActive]}
            activeOpacity={0.8}
          >
            <Ionicons
              name="videocam-outline"
              size={15}
              color={currentTabIndex === 3 ? '#6C2BD9' : '#6B7280'}
              style={{ marginRight: 6 }}
            />
            <Text style={[styles.tabText, currentTabIndex === 3 && styles.tabTextActive]}>
              Videos
            </Text>
          </TouchableOpacity>
        </View>

        {/* Active Tab Content */}
        <View style={styles.tabContentArea}>
          {currentTabIndex === 1 && <MediaBlogHub {...props} searchQuery={searchQuery} />}
          {currentTabIndex === 2 && <MediaNewsHub {...props} searchQuery={searchQuery} />}
          {currentTabIndex === 3 && <MediaVideoHub {...props} searchQuery={searchQuery} />}
        </View>

        <View style={{ height: 40 }} />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: '#F5F7FB',
    overflow: 'hidden',
  },
  breadcrumbBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 24,
    paddingTop: 14,
    paddingBottom: 4,
    flexShrink: 0,
  },
  breadcrumbBarMobile: {
    paddingHorizontal: 12,
    paddingTop: 10,
    paddingBottom: 2,
  },
  breadItem: {
    fontSize: 12,
    color: '#9CA3AF',
  },
  breadSep: {
    fontSize: 12,
    color: '#9CA3AF',
  },
  breadActive: {
    fontSize: 12,
    color: '#6C2BD9',
    fontWeight: '600',
  },
  scrollContent: {
    paddingHorizontal: 24,
    paddingTop: 12,
    paddingBottom: 40,
  },
  scrollContentMobile: {
    paddingHorizontal: 12,
    paddingTop: 8,
    paddingBottom: 50,
  },
  headerSection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    gap: 16,
  },
  headerSectionMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 16,
  },
  headerTextWrap: {
    flex: 1,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: COLORS.textPrimary,
  },
  pageSubtitle: {
    fontSize: 13,
    color: COLORS.textSecondary,
    marginTop: 2,
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    paddingHorizontal: 14,
    height: 40,
    width: 280,
    ...SHADOWS.sm,
  },
  searchBoxMobile: {
    width: '100%',
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: '#111827',
    paddingVertical: 0,
    outlineStyle: 'none',
  },
  tabBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 30,
    padding: 4,
    marginBottom: 20,
    alignSelf: 'flex-start',
    borderWidth: 1,
    borderColor: '#F3F4F6',
    ...SHADOWS.sm,
  },
  tabBarMobile: {
    width: '100%',
  },
  tabBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 24,
  },
  tabBtnMobile: {
    flex: 1,
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  tabBtnActive: {
    backgroundColor: '#F0E8FF',
  },
  tabText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#6B7280',
  },
  tabTextActive: {
    color: '#6C2BD9',
    fontWeight: '700',
  },
  tabContentArea: {
    width: '100%',
  },
});

export default drawerHoc({
  Component: MediaHub,
  showBackButton: true,
  noScroll: true,
});
